import { PencilIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectDetailResponse } from '@/entities/project/api/generated/model';
import { ProjectFormTagSelect } from '@/features/projects/project-form/ui/ProjectFormTagSelect';
import { Button, Skeleton, Text } from '@/shared/ui';
import { TagList } from '@/shared/ui/tag-list/TagList';
import { useProjectTagsUpdate } from '../model/useProjectTagsUpdate';

type Props = {
  projectId: string;
  detail: ProjectDetailResponse | undefined;
  isLoading: boolean;
};

/**
 * 事業パネルの「管理」セクション内のタグ表示・編集。
 *
 * 査定（assessment）の AssessmentComparisonTagSection と同じ UI・挙動に揃える：
 * - ラベル + 鉛筆「編集」ボタン（編集中は「編集を終了」に切替）
 * - 編集中はインラインでタグ選択 UI（ProjectFormTagSelect）を展開し、変更を即時保存する
 * - 非編集時は TagList で表示し、タグなしは Text の "--" 表示にフォールバックする
 *
 * タグ更新は事業編集の更新 API（useProjectTagsUpdate 経由）を利用する（ITA-2721）。
 */
export const ProjectTagsManagementSection = ({ projectId, detail, isLoading }: Props) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const { updateTags } = useProjectTagsUpdate(projectId);

  const assignedTags = (detail?.tags ?? []).flatMap((tag) =>
    tag.id && tag.name ? [{ id: tag.id, name: tag.name }] : [],
  );

  const toggleEditing = () => {
    if (!isEditing) {
      // 編集開始時に現在の付与タグで初期化する
      setSelectedTagIds(assignedTags.map((tag) => tag.id));
    }
    setIsEditing((v) => !v);
  };

  // 査定と同様、変更を即時保存する（追加・削除のたびに事業更新 API を呼ぶ）。
  const handleChange = (nextTagIds: string[]) => {
    setSelectedTagIds(nextTagIds);
    if (detail) {
      updateTags(detail, nextTagIds);
    }
  };

  return (
    // pr-1: Accordion content の overflow-hidden で「編集」ボタンの focus ring 右端が見切れないよう余白を確保
    <div className="flex flex-col gap-1.5 pr-1">
      <div className="flex items-center justify-between pt-1">
        <Text variant="label">{t('project.fields.tags')}</Text>
        <Button variant="ghost" onClick={toggleEditing} disabled={!detail}>
          {isEditing ? (
            t('common.actions.finish-edit')
          ) : (
            <>
              <PencilIcon className="h-4 w-4" />
              {t('common.actions.edit')}
            </>
          )}
        </Button>
      </div>

      {isEditing ? (
        <ProjectFormTagSelect
          selectedTagIds={selectedTagIds}
          onSelectedTagIdsChange={handleChange}
        />
      ) : isLoading ? (
        <Skeleton className="h-6 w-40" />
      ) : assignedTags.length > 0 ? (
        <TagList tags={assignedTags} maxDisplay={assignedTags.length} />
      ) : (
        <Text>{null}</Text>
      )}
    </div>
  );
};
